import { type TFunction } from "i18next";

import { type OverviewData } from "../../types";
import { type OverviewTile, type TileEntityType } from "./types";

const numberFormatter = new Intl.NumberFormat("en-US");

const formatCount = (value: number | undefined | null): string =>
  numberFormatter.format(typeof value === "number" && Number.isFinite(value) ? value : 0);

interface OverviewTilesInput {
  /** The `overview` block (may be partial while loading). */
  totals: Partial<OverviewData>;
  /** Count of aggregator administrators (`/api/aggregator_administrators/`). */
  administratorsCount: number;
  /** Count of buyer representatives (`/api/trader_representatives/`). */
  representativesCount: number;
  /** External users have no access to the farmers list, so its "View" link is hidden. */
  isExternalUser?: boolean;
}

/**
 * Builds the three overview tiles from the `overview` block plus the administrator
 * and representative counts. Each tile carries a single metric line whose trailing
 * action links to the matching list page.
 */
export const adaptOverviewTiles = (
  { totals, administratorsCount, representativesCount, isExternalUser = false }: OverviewTilesInput,
  t: TFunction
): Record<TileEntityType, OverviewTile> => ({
  aggregator: {
    entityType: "aggregator",
    total: formatCount(totals.aggregators_registered),
    viewAllTo: "/wfpManager/aggregators",
    subMetrics: [
      {
        id: "mobileAppUsers",
        label: t("home.manager.overview.tiles.aggregators.mobileAppUsers"),
        value: formatCount(administratorsCount),
        action: {
          ariaLabel: t("home.manager.overview.viewAdministrators"),
          to: "/wfpManager/administrators",
        },
      },
    ],
  },
  farmer: {
    entityType: "farmer",
    total: formatCount(totals.farmers_registered),
    // External users cannot access the farmers list; omit the header "View" link.
    viewAllTo: isExternalUser ? undefined : "/wfpManager/farmers",
    subMetrics: [
      {
        id: "availableCommodities",
        label: t("home.manager.overview.tiles.farmers.availableCommodities"),
        value: formatCount(totals.commodities_available),
        action: {
          ariaLabel: t("home.manager.overview.viewCommodities"),
          to: "/wfpManager/commodities",
        },
      },
    ],
  },
  buyer: {
    entityType: "buyer",
    total: formatCount(totals.buyers_registered),
    viewAllTo: "/wfpManager/traders",
    subMetrics: [
      {
        id: "webAppUsers",
        label: t("home.manager.overview.tiles.buyers.webAppUsers"),
        value: formatCount(representativesCount),
        action: {
          ariaLabel: t("home.manager.overview.viewBuyerRepresentatives"),
          to: "/wfpManager/buyerRepresentatives",
        },
      },
    ],
  },
});
